/**
 * Cognite-specific type definitions and type guards.
 */
import { z } from 'zod';

/**
 * Instance reference shape used in CDF Data Models.
 * References a specific node or edge in a space.
 */
export interface InstanceReference {
  space: string;
  externalId: string;
}

/**
 * Zod schema for strict instance reference validation.
 * Rejects objects with extra properties beyond space and externalId.
 */
export const instanceReferenceSchema = z.object({ space: z.string(), externalId: z.string() }).strict();

/**
 * Type guard to check if a value is a valid InstanceReference.
 * Non-strict: allows extra properties.
 *
 * @param value - The value to check
 * @returns True if the value has space and externalId string properties
 *
 * @example
 * if (isInstanceReference(value)) {
 *   console.log(value.space, value.externalId);
 * }
 */
export function isInstanceReference(value: unknown): value is InstanceReference {
  return (
    typeof value === 'object' &&
    value !== null &&
    'space' in value &&
    'externalId' in value &&
    typeof (value as InstanceReference).space === 'string' &&
    typeof (value as InstanceReference).externalId === 'string'
  );
}

/**
 * Type guard to check if a value is a strict InstanceReference.
 * Strict: rejects objects with extra properties.
 *
 * @param value - The value to check
 * @returns True if the value is exactly { space: string, externalId: string }
 */
export function isStrictInstanceReference(value: unknown): value is InstanceReference {
  return instanceReferenceSchema.safeParse(value).success;
}
